/**
 * 알림 UI 헬퍼 함수들
 */

import { UserPlus, Users, Clock, Mail, FileText, CheckCircle, XCircle, Sparkles } from 'lucide-react'
import type { NotificationMessage } from '@/types/notification'
import type { ReactElement } from 'react'

/**
 * 메시지 타입별 아이콘 반환
 */
export const getMessageIcon = (type: string): ReactElement => {
  switch (type) {
    case 'team_invite':
      return <UserPlus className="w-5 h-5 text-blue-500" />
    case 'team_proposal':
      return <Users className="w-5 h-5 text-purple-500" />
    case 'join_request':
      return <Users className="w-5 h-5 text-green-500" />
    case 'sent_join_request':
      return <Clock className="w-5 h-5 text-amber-500" />
    case 'client_to_team_estimate_request':
    case 'client_to_individual_estimate_request':
    case 'team_to_client_estimate_request': // Client scope: 팀→기업 견적요청
      return <FileText className="w-5 h-5 text-blue-500" />
    case 'estimate_submitted':
      return <FileText className="w-5 h-5 text-green-500" />
    case 'estimate_accepted':
      return <CheckCircle className="w-5 h-5 text-emerald-500" />
    case 'estimate_rejected':
      return <XCircle className="w-5 h-5 text-red-500" />
    case 'project_matched':
    case 'counsel_matched_to_team':
      return <Sparkles className="w-5 h-5 text-orange-500" />
    case 'public_inquiry_created':
      return <Mail className="w-5 h-5 text-indigo-500" />
    default:
      return <Mail className="w-5 h-5 text-gray-400" />
  }
}

/**
 * 메시지 타입별 배경색 반환
 */
export const getMessageBgColor = (type: string): string => {
  switch (type) {
    case 'team_invite':
      return 'bg-blue-50'
    case 'team_proposal':
      return 'bg-purple-50'
    case 'join_request':
      return 'bg-green-50'
    case 'sent_join_request':
      return 'bg-amber-50'
    case 'client_to_team_estimate_request':
    case 'client_to_individual_estimate_request':
    case 'team_to_client_estimate_request': // Client scope: 팀→기업 견적요청
      return 'bg-blue-50'
    case 'estimate_submitted':
      return 'bg-green-50'
    case 'estimate_accepted':
      return 'bg-emerald-50'
    case 'estimate_rejected':
      return 'bg-red-50'
    case 'project_matched':
    case 'counsel_matched_to_team':
      return 'bg-orange-50'
    case 'public_inquiry_created':
      return 'bg-indigo-50'
    default:
      return 'bg-gray-50'
  }
}

/**
 * 성공 메시지 맵
 */
export const getSuccessMessage = (type: string, action: 'accept' | 'decline'): string => {
  const successMessages: Record<string, Record<'accept' | 'decline', string>> = {
    team_invite: {
      accept: '팀 초대를 수락했습니다',
      decline: '팀 초대를 거절했습니다',
    },
    team_proposal: {
      accept: '팀 제안을 수락했습니다',
      decline: '팀 제안을 거절했습니다',
    },
    team_join_request: {
      accept: '합류 신청을 수락했습니다',
      decline: '합류 신청을 거절했습니다',
    },
    join_request: {
      accept: '합류 신청을 수락했습니다',
      decline: '합류 신청을 거절했습니다',
    },
    client_to_team_estimate_request: {
      accept: '견적 요청을 수락했습니다',
      decline: '견적 요청을 거절했습니다',
    },
    team_to_client_estimate_request: {
      accept: '견적 요청 상태를 갱신했습니다',
      decline: '견적 요청을 거절했습니다',
    },
    client_to_individual_estimate_request: {
      accept: '견적 요청이 전송되었습니다',
      decline: '견적 요청이 취소되었습니다',
    },
    estimate_submitted: {
      accept: '견적서를 수락했습니다',
      decline: '견적서를 거절했습니다',
    },
    estimate_accepted: {
      accept: '견적서가 채택되었습니다',
      decline: '견적서가 거절되었습니다',
    },
  }

  const messageType = type === 'join_request' ? 'team_join_request' : type
  return successMessages[messageType]?.[action] || (action === 'accept' ? '수락 완료' : '거절 완료')
}

/**
 * 날짜 포맷팅
 */
export const formatNotificationDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('ko-KR', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}



